import React, { useEffect, useState } from "react";
import AdminLayout from "../../Layout/index.jsx";
import { useParams } from "react-router-dom";
import { IoMdArrowRoundBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import Pagination from "../../../../components/Pagination/index.jsx";
import {
  useGetClustersByCampusQuery,
  useGetAllCoursesByClusterQuery,
  useGetStaffByEmailIdQuery,
} from "../../../../graphql/generated.tsx";
import { auth } from "../../../../utils/firebase.js";
import Loader from "../../../../components/Loader.jsx";

const ClusterCard = ({ cluster, campusName, onClick }) => {
  const { data: coursesData } = useGetAllCoursesByClusterQuery({
    variables: { campusName, clusterId: cluster.id },
    skip: !campusName || !cluster?.id,
  });

  const courseList = (coursesData?.getAllCoursesByCluster || [])
    .map((course) => course?.name)
    .filter(Boolean);

  const visibleCourses = courseList.slice(0, 3);
  const remainingCoursesCount = Math.max(0, courseList.length - visibleCourses.length);

  return (
    <div
      onClick={onClick}
      className="group relative cursor-pointer rounded-2xl overflow-hidden border border-gray-200 bg-[#f4f7fa] font-poppins shadow-[0_4px_12px_rgba(0,0,0,0.06)] transition-all duration-300 hover:border-[#0e3d5d]/30"
    >
      <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-black/[0.02]" />
      {/* top accent line */}
      <div className="h-[3px] w-0 group-hover:w-full bg-gradient-to-r from-[#0e3d5d] to-[#3a7fa8] transition-all duration-300" />

      <div className="p-6">
        {/* header: name + total students (right) */}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] tracking-[0.14em] text-gray-400">BATCH DETAILS</p>
            <h3 className="text-[18px] font-[700] tracking-[-0.3px] text-[#0f172a] mt-1 truncate">
              {cluster.name}
            </h3>
          </div>

          <div className="shrink-0 text-right">
            <div className="text-[11px] tracking-[0.18em] text-gray-400">
              ENROLLED STUDENT
            </div>
            <div className="text-2xl font-normal leading-tight text-[#0e3d5d]">
              {cluster.studentCount}
            </div>
          </div>
        </div>

        <div className="mt-4 h-px w-full bg-gradient-to-r from-[#e7eef6] via-[#dbe7f3] to-transparent" />

        {/* course names + arrow */}
        <div className="mt-3">
          <div className="text-[11px] tracking-[0.14em] text-gray-400 mb-2">
            COURSE NAME
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              {courseList.length === 0 ? (
                <div className="text-[12px] text-gray-500">No courses assigned</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {visibleCourses.map((name) => (
                    <span
                      key={name}
                      className="text-[15px] font-semibold leading-none text-[#0e3d5d]"
                    >
                      {name}
                    </span>
                  ))}

                  {remainingCoursesCount > 0 ? (
                    <span className="text-[14px] font-semibold leading-none text-[#0e3d5d]">
                      +{remainingCoursesCount} more
                    </span>
                  ) : null}
                </div>
              )}
            </div>

            <button
              type="button"
              aria-label="Open cluster report"
              onClick={(e) => {
                e.stopPropagation();
                onClick?.();
              }}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-[#f1f5f9] border border-[#e2e8f0] text-gray-500 text-lg font-semibold leading-none transition-all duration-200 group-hover:bg-[#0e3d5d] group-hover:border-[#0e3d5d] group-hover:text-white group-hover:shadow-[0_0_0_6px_rgba(14,61,93,0.12)]"
            >
              →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ReportsClusterListing = () => {
  const { campusName } = useParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoad, setIsLoad] = useState(true);
  const [page, setPage] = useState(1);
  const limit = 9;

  const [user, setUser] = useState();

  useEffect(() => {
    setTimeout(() => {
      setIsLoad(false);
    }, 2000);
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUser(user);
      } else {
        setUser(null);
      }
    });
    return () => { unsubscribe(); };
  }, []);

  const userRole = user
    ? JSON.parse(user?.reloadUserInfo?.customAttributes).role
    : null;

  const { data: staffData, loading: staffLoading } = useGetStaffByEmailIdQuery({
    variables: { email: user?.email },
  });

  const { data, refetch } = useGetClustersByCampusQuery({
    variables: { campusName, searchTerm, page: 1, limit: 200 },
  });

  const availableClusters =
    userRole === "staff"
      ? staffData?.getStaffByEmailId?.isAdminStaff
        ? data?.getClustersByCampus
        : data?.getClustersByCampus.filter((cluster) =>
            staffData?.getStaffByEmailId?.clusterIds.includes(cluster.id)
          )
      : data?.getClustersByCampus;

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const clusters = normalizedSearch
    ? (availableClusters || []).filter((cluster) =>
        (cluster?.name || "").toLowerCase().includes(normalizedSearch)
      )
    : availableClusters || [];
  const totalPages = Math.ceil(clusters.length / limit);
  const pagedClusters = clusters.slice((page - 1) * limit, page * limit);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
            refetch({ campusName, searchTerm: e.target.value, page: 1, limit: 200 });
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    refetch({ campusName, searchTerm, page: 1, limit: 200 });
  };

  return (
    <AdminLayout>
      {isLoad ? (
        <div className="flex items-center justify-center h-screen">
          <Loader />
        </div>
      ) : (
      <div className="max-w-full p-5 bg-white min-h-screen">
        <div className="flex items-center justify-between mb-8 max-md:flex-col max-md:gap-4">
          <div className="flex items-center">
            <button
              onClick={() => window.history.back()}
              className="text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <IoMdArrowRoundBack className="w-6 h-6" />
            </button>
            <div className="ml-2 text-2xl font-bold max-md:text-lg">
              {campusName} Clusters
            </div>
          </div>
          <div className="w-full mt-2 md:w-auto">
            <input
              type="text"
              placeholder="Search by name"
              value={searchTerm}
              onChange={handleSearch}
              className="w-full p-2 border border-gray-300 rounded-md md:max-w-xs"
            />
          </div>
        </div>

        {/* ✦ ONLY THE CARD IS CHANGED BELOW ✦ */}
        <div className="grid grid-cols-1 gap-5 mt-10 md:grid-cols-2 lg:grid-cols-3">
          {pagedClusters.map((cluster) => (
            <ClusterCard
              key={cluster.id}
              cluster={cluster}
              campusName={campusName}
              onClick={() => navigate(`${cluster.id}`)}
            />
          ))}
        </div>

        {totalPages > 1 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>
      )}
    </AdminLayout>
  );
};

export default ReportsClusterListing;